import React, { FC } from "react";

import { useTranslation } from "react-i18next";
import { Button, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { DocumentIcon, SearchYellowIcon } from "@/assets/icons";
import { HiddenOnScope } from "@/components/atoms";
import { EnvironmentSwitch, FilterChipList, FilterDropdown, PolicyholderDropdown } from "@/components/organisms";
import LocalFilterChipList from "@/components/organisms/FilterChipList/LocalFilterChipList";
import { IFilterItem } from "@/shared/contexts/localFilterContext";
import { usePolicyholders } from "@/shared/hooks";

import Breadcrumbs from "../Breadcrumbs/Breadcrumbs";
import { StyledPaginatedListOptionItem, StyledSubHeader, StyledSubHeaderContainer } from "./styled";

type AuthenticatedSubHeaderProps = {
    onOpenSearch?: () => void;
    onOpenLinkedDocuments?: () => void;
    localFilters?: IFilterItem[];
    showGlobalFilter?: boolean;
    showEnvironmentSwitch?: boolean;
    displayLinkedDocumentsButton?: boolean;
};

const AuthenticatedSubHeader: FC<AuthenticatedSubHeaderProps> = ({
    onOpenSearch,
    onOpenLinkedDocuments,
    localFilters,
    showGlobalFilter = false,
    showEnvironmentSwitch = false,
    displayLinkedDocumentsButton,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const { getPolicyholders, existPhysicalPolicyholders, existMoralPolicyholders } = usePolicyholders();

    const policyholders = getPolicyholders();
    const displayEnvironmentSwitch = showEnvironmentSwitch && existPhysicalPolicyholders && existMoralPolicyholders;
    const displayPolicyholderDropdown = showGlobalFilter && policyholders?.length > 1;
    const displayPaginatedListOption = !!onOpenSearch;
    const displayChipList = !isSmallScreen && showGlobalFilter;
    const displayLocalChipList = !isSmallScreen && localFilters;
    const displayFilterRow =
        showEnvironmentSwitch ||
        displayPolicyholderDropdown ||
        displayPaginatedListOption ||
        displayChipList ||
        displayLocalChipList;
    return (
        <StyledSubHeader>
            <StyledSubHeaderContainer className="subHeaderContainer">
                <HiddenOnScope>
                    <Breadcrumbs />
                </HiddenOnScope>
                {displayFilterRow && (
                    <div className="d-flex align-center filterRow">
                        {displayEnvironmentSwitch && <EnvironmentSwitch />}
                        {displayPolicyholderDropdown && (
                            <PolicyholderDropdown title={t("PolicyHolders.PolicyHolders")} />
                        )}
                        {displayPaginatedListOption && (
                            <StyledPaginatedListOptionItem>
                                <Button
                                    className="paginatedListOptionIcon"
                                    disableRipple
                                    data-testid="paginated-list-link-search"
                                    onClick={() => onOpenSearch()}
                                >
                                    <SearchYellowIcon />
                                </Button>
                                <span className="paginatedListOptionLabel">{t("Common.Search")}</span>
                            </StyledPaginatedListOptionItem>
                        )}
                        {localFilters && <FilterDropdown title={t("Common.Filter")} filters={localFilters} />}
                        {displayChipList && <FilterChipList />}
                        {displayLocalChipList && <LocalFilterChipList filters={localFilters} />}

                        <div className="spacer"></div>
                        {displayLinkedDocumentsButton && (
                            <StyledPaginatedListOptionItem
                                data-testid="paginated-list-link-documents"
                                onClick={() => onOpenLinkedDocuments()}
                            >
                                <Button className="paginatedListOptionIcon" disableRipple>
                                    <DocumentIcon />
                                </Button>
                                <span className="paginatedListOptionLabel">{t("Common.Documents")}</span>
                            </StyledPaginatedListOptionItem>
                        )}
                    </div>
                )}
            </StyledSubHeaderContainer>
        </StyledSubHeader>
    );
};

export default AuthenticatedSubHeader;
